import React from 'react';
import PropTypes from 'prop-types';
// @mui
import { Alert, Box } from '@mui/material';
// components
import WidgetButtons from './WidgetButtons';
import { ActionCard, ActionCardSkeleton, URGENCY_STATES } from '../actionCards/ActionCards';
import { STAGE_ACCENT_COLORS, CHIP_GREEN } from '../actionCards/actionCardColors';
import { buildTotalChip, pluralize } from '../actionCards/totalChip';
import BookingPodiumWidget from './BookingPodiumWidget';
// redux
import { useSelector } from '../../redux/store';
// hooks
import { useToggle } from '../../hooks';
import { STAGE_ICONS } from '../../constants/stageIcons';

// Tooltips for each widget
const WIDGET_TOOLTIPS = {
    // Task L (2026-10-03): "Upcoming" now counts ALL future bookings, not just
    // the next 7 days (that narrower window lives on the card's "N this week"
    // chip instead — see UPCOMING_WEEK_CHIP_KEY below).
    upcoming: 'All consultations booked from today onwards.',
    // Task N (2026-10-04): "Past, no outcome" -> "Awaiting outcome" (copy only — key stays 'past').
    past: 'Appointments awaiting an outcome — needs attendance to be recorded. Mark as attended or FTA.',
};

// The chip's own scope key — a real task_due_bucket-style bookedWidgetFilter
// value (handled in coreThunks.js's bookedWidgetFilter switch and
// resolveBookedWidgetDateWindow), not just a UI label. Clicking it narrows the
// "Upcoming" card's all-future scope down to today..+7.
const UPCOMING_WEEK_CHIP_KEY = 'upcoming_week';

// Shared flex row style — mirrors ActionCards' cardWrapperStyle so the three
// cards (Upcoming, Past, Top Bookers) sit as equal-width siblings in one row.
const rowStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    width: '100%',
    mb: 3,
};

const BookedTableReportsWidget = ({ onWidgetClick = () => {}, dateRangeProps, selectedWidget, widgetToggle, practiceId, liveCounts, liveCountsLoading, liveCountsError }) => {
    const { toggle: internalToggle, onOpen, onClose } = useToggle(true);
    const toggle = widgetToggle !== undefined ? widgetToggle : internalToggle;
    const { widgetCounter, loadedKeys, expectedCount } = useSelector((state) => state.widgetCounter);

    const handleOpen = () => onOpen();
    const handleClose = () => onClose();

    // Widget configurations
    const cardConfigs = [
        { key: 'upcoming', label: 'Upcoming', icon: STAGE_ICONS.BOOKED, iconBgColor: '#dbeafe' },
        { key: 'past', label: 'Awaiting outcome', icon: STAGE_ICONS.BOOKED, iconBgColor: '#fee2e2' },
    ];

    // Get count from widget counter
    const getCount = (key) => {
        if (liveCounts) return liveCounts[key];
        const counter = widgetCounter?.find((c) => c.key === key);
        return counter?.count || 0;
    };

    // Task O: both cards' headlines now take a fixed Booked-stage (purple) accent
    // instead of urgency-driven red/amber/green — see STAGE_ACCENT_COLORS. Always
    // NEUTRAL here; urgencyState is kept only because ActionCard's wantsClearHint /
    // resolveSelectionColor read it, and NEUTRAL already gives the correct slate
    // selection ring (see actionCardColors.js's resolveSelectionColor).
    const getUrgencyState = () => URGENCY_STATES.NEUTRAL;

    // Generate badges for each widget — 'upcoming' no longer uses this (it's
    // isV2Split now, see splitChips below, which replaces the badge row).
    //
    // Task O: RED is reserved for "Overdue" chips only — "Record outcome" isn't an
    // Overdue chip (this card can read "awaiting outcome" even for a FUTURE
    // appointment that simply hasn't had its outcome recorded yet), so it's now an
    // amber "needs attention" tone, matching the Launchpad Booked card's own
    // "Awaiting outcome" chip.
    const generateBadges = (key, count) => {
        const badges = [];
        if (key === 'past' && count > 0) {
            badges.push({ type: 'due-today', text: 'Record outcome', showDot: true, dotColor: '#d97706' });
        }
        if (count === 0 && key === 'past') {
            badges.push({ type: 'on-track', text: 'All recorded', showDot: true, dotColor: CHIP_GREEN.text });
        }
        // The "Clear to see all" hint for a selected zero-count card is added centrally by
        // ActionCard (see wantsClearHint there) so every board behaves identically — this
        // widget deliberately does not duplicate it.
        return badges;
    };

    // Task L: clicking the chip or the card body both route through
    // onWidgetClick (same prop the 'past' card already uses) — NewLead.jsx's
    // onWidgetClick special-cases 'upcoming'/'upcoming_week' to implement the
    // "chip falls back to the card's own scope, card body clears across
    // either" toggle (a plain same-value-clears-to-null toggle can't express
    // that two scopes belong to one card).
    const handleChipClick = (e) => {
        e.stopPropagation();
        onWidgetClick(UPCOMING_WEEK_CHIP_KEY);
    };

    // Grey "N booked" total chip in the Upcoming card (unscoped table count). Selected when NO scope
    // is applied (landing); clicking it clears the scope (same click path as the card body:
    // 'upcoming' clears the upcoming family, any other active key toggles itself off).
    const bookedTotal = liveCounts && typeof liveCounts.total === 'number' ? liveCounts.total : null;
    const isUnscoped = !selectedWidget;
    const clearScope = () => {
        if (!selectedWidget) return;
        onWidgetClick(selectedWidget === UPCOMING_WEEK_CHIP_KEY ? 'upcoming' : selectedWidget);
    };
    const totalChip = bookedTotal === null ? null : buildTotalChip({
        testId: 'booked-total-chip',
        text: `${bookedTotal.toLocaleString()} booked`,
        tooltip: `${bookedTotal.toLocaleString()} booked ${pluralize(bookedTotal, 'consultation', 'consultations')} in total — click to show all`,
        selected: isUnscoped,
        onClick: clearScope,
    });

    // Build card data
    const cards = cardConfigs.map((config) => {
        const count = getCount(config.key);
        const hasCount = typeof count === 'number';
        const isUpcoming = config.key === 'upcoming';
        const weekCount = isUpcoming ? getCount(UPCOMING_WEEK_CHIP_KEY) : null;
        const hasWeekCount = typeof weekCount === 'number';
        return {
            key: config.key,
            count: hasCount ? count : 0,
            displayCount: hasCount ? count : '—',
            label: config.label,
            icon: config.icon,
            iconBgColor: config.iconBgColor,
            urgencyState: hasCount ? getUrgencyState(config.key, count) : URGENCY_STATES.NEUTRAL,
            // Task O: Booked stage accent (purple) on both cards — see STAGE_ACCENT_COLORS.
            accentColor: STAGE_ACCENT_COLORS.BOOKED,
            badges: (hasCount && !isUpcoming) ? generateBadges(config.key, count) : [],
            tooltip: WIDGET_TOOLTIPS[config.key],
            disabled: !hasCount,
            // Task L: the "Upcoming" card's "N this week" chip — generic
            // splitChips row (ActionCards.jsx), same pattern as the
            // Attended tab's "Due Now" Overdue/Due Today chips and the
            // New-lead table's "No active task" Checklist complete/No tasks
            // chips. One chip here, not a pair.
            ...(isUpcoming ? {
                totalChip,
                fadeEmphasis: isUnscoped && totalChip !== null,
                isV2Split: true,
                splitChips: [
                    {
                        bucket: UPCOMING_WEEK_CHIP_KEY,
                        text: `${hasWeekCount ? weekCount : '—'} this week`,
                        type: 'on-track',
                        // Task O rule 5: the dot colour follows the chip's own tone — this
                        // chip is 'on-track' (green), so the dot must be green too, not blue.
                        dotColor: CHIP_GREEN.text,
                        onClick: hasWeekCount ? handleChipClick : undefined,
                        selected: selectedWidget === UPCOMING_WEEK_CHIP_KEY,
                    },
                ],
            } : {}),
        };
    });

    const isLoading = liveCounts ? liveCountsLoading && cards.every((card) => card.displayCount === '—') : expectedCount > 0 && loadedKeys.length < expectedCount;

    // Handle card click — pass key to parent for table filtering
    const handleCardClick = (card) => {
        onWidgetClick(card.key);
    };

    // The merged "Upcoming" card reads as selected for EITHER its own scope
    // or the chip's narrower one — selectedWidget alone (strict `=== card.key`)
    // would miss 'upcoming_week'.
    const isCardSelected = (card) => (
        card.key === 'upcoming'
            ? (selectedWidget === 'upcoming' || selectedWidget === UPCOMING_WEEK_CHIP_KEY)
            : selectedWidget === card.key
    );

    return (
        <>
            {dateRangeProps && <WidgetButtons
                toggle={toggle}
                dateRangeProps={dateRangeProps}
                handleOpen={handleOpen}
                handleClose={handleClose}
                type="ConsultantBooked"
            />}
            {toggle && (
                <>
                    {liveCountsError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Booked widget counts could not be refreshed.
                        </Alert>
                    )}
                    {/* Outer flex row: two ActionCards + the Top Bookers podium card
                        as equal-width siblings (flex:1 1 0 each). */}
                    <Box sx={rowStyle}>
                    {isLoading ? (
                        // Show skeleton placeholders for the two counter cards while loading
                        <>
                            <ActionCardSkeleton />
                            <ActionCardSkeleton />
                        </>
                    ) : (
                        cards.map((card) => (
                            <ActionCard
                                key={card.key}
                                card={card}
                                onClick={handleCardClick}
                                isSelected={isCardSelected(card)}
                                isLoaded={liveCounts ? card.displayCount !== '—' : loadedKeys?.includes(card.key)}
                            />
                        ))
                    )}

                    {/* Top Bookers — 3rd card, same flex sizing as the ActionCards above.
                        variant='widget' renders it as an ActionCard-matched flex item. */}
                    {practiceId && (
                        <BookingPodiumWidget
                            practiceId={practiceId}
                            rankField='appointments_booked'
                            title='Top Bookers — This Month'
                            countLabel='booked'
                            variant='widget'
                        />
                    )}
                    </Box>
                </>
            )}
        </>
    );
};

export default BookedTableReportsWidget;

BookedTableReportsWidget.propTypes = {
    onWidgetClick: PropTypes.func,
    dateRangeProps: PropTypes.any,
    selectedWidget: PropTypes.string,
    widgetToggle: PropTypes.bool,
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    liveCounts: PropTypes.shape({ upcoming: PropTypes.number, upcoming_week: PropTypes.number, past: PropTypes.number }),
    liveCountsLoading: PropTypes.bool,
    liveCountsError: PropTypes.string,
};

BookedTableReportsWidget.defaultProps = {
    onWidgetClick: () => {},
    practiceId: null,
    liveCounts: null,
    liveCountsLoading: false,
    liveCountsError: null,
};
